import React, { useState } from "react";

import {
  Box,
  CircularProgress,
  InputAdornment,
  MenuItem,
  TextField,
} from "@mui/material";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Select from "@mui/material/Select";
import { makeStyles, withStyles } from "tss-react/mui";

import SearchIcon from "@/components/Icons/SearchIcon";
import { FORM_ELEMENT_KINDS } from "@/config/constants";
import { Features } from "@/config/features";
import { FormElementKind } from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";

import Affirmation from "./components/LeftPanel/Affirmation";
import Date from "./components/LeftPanel/Date";
import Description from "./components/LeftPanel/Description";
import Dropdown from "./components/LeftPanel/Dropdown";
import Files from "./components/LeftPanel/Files";
import Header from "./components/LeftPanel/Header";
import ImageAndVideos from "./components/LeftPanel/ImageAndVideos";
import LongText from "./components/LeftPanel/LongText";
import MediaUrl from "./components/LeftPanel/MediaUrl";
import Number from "./components/LeftPanel/Number";
import ShortText from "./components/LeftPanel/ShortText";
import Signature from "./components/LeftPanel/Signature";
import VerticalMultipleSelector from "./components/LeftPanel/VerticalMultipleSelector";
import VerticalSingleSelector from "./components/LeftPanel/VerticalSingleSelector";
import { FormElementAttributesType } from "./components/utils/types";

const useStyles = makeStyles()(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: colors.white,
    width: "30%",
    marginRight: 14,
    padding: 12,
    overflowY: "scroll",
  },
  searchBar: {
    height: 8,
    width: "100%",
  },
  listItem: {
    "&.MuiListItem-gutters": {
      padding: 0,
    },
  },
}));

const StyledTextField = withStyles(TextField, (theme) => ({
  root: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
  },
}));

const components = FORM_ELEMENT_KINDS;

const ComponentsCard: React.FC = () => {
  const { classes } = useStyles();
  const [searchComponentResults, setSearchComponentResults] =
    useState<FormElementAttributesType[] | []>(components);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");

  const handleSearchComponents = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSearchLoading(true);
    const value = event.target.value;
    setSearchText(value);
    const results = components.filter((c) =>
      c.kind.includes(event.target.value.toLocaleUpperCase())
    );
    setSearchComponentResults(results);
    setSearchLoading(false);
  };

  return (
    <Box className={classes.root}>
      {Features.formCategoriesEnabled && (
        <Select
          variant="outlined"
          style={{ width: "100%", marginBottom: 12 }}
          labelId="demo-simple-select-label"
          id="simple-select"
          value={10}
        >
          <MenuItem value={10}>All</MenuItem>
          <MenuItem value={20}>Charting</MenuItem>
          <MenuItem value={30}>HIPAA</MenuItem>
          <MenuItem value={40}>Agreement</MenuItem>
        </Select>
      )}
      <StyledTextField
        placeholder="Search elements"
        InputProps={{
          endAdornment: (
            <InputAdornment position="start">
              <SearchIcon style={{ color: "#26C9A7" }} />
            </InputAdornment>
          ),
          classes: { input: classes.searchBar },
        }}
        onChange={handleSearchComponents}
        value={searchText}
        variant="outlined"
      />

      <List>
        {searchLoading && (
          <Box
            padding={12}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flex={1}
          >
            <CircularProgress />
          </Box>
        )}
        {!searchLoading &&
          searchComponentResults.map((e, index) => {
            switch (e.kind) {
              case FormElementKind.Header:
                return (
                  <ListItem key={index} className={classes.listItem}>
                    <Header componentType={e} />
                  </ListItem>
                );
              case FormElementKind.Description:
                return (
                  <ListItem key={index} className={classes.listItem}>
                    <Description componentType={e} />
                  </ListItem>
                );
              case FormElementKind.LongText:
                return (
                  <ListItem key={index} className={classes.listItem}>
                    <LongText componentType={e} />
                  </ListItem>
                );
              case FormElementKind.ShortText:
                return (
                  <ListItem key={index} className={classes.listItem}>
                    <ShortText componentType={e} />
                  </ListItem>
                );
              case FormElementKind.Dropdown:
                return (
                  <ListItem key={index} className={classes.listItem}>
                    <Dropdown componentType={e} />
                  </ListItem>
                );
              case FormElementKind.Number:
                return (
                  <ListItem key={index} className={classes.listItem}>
                    <Number componentType={e} />
                  </ListItem>
                );
              case FormElementKind.VerticalSingleSelector:
                return (
                  <ListItem key={index} className={classes.listItem}>
                    <VerticalSingleSelector componentType={e} />
                  </ListItem>
                );
              case FormElementKind.VerticalMultipleSelector:
                return (
                  <ListItem key={index} className={classes.listItem}>
                    <VerticalMultipleSelector componentType={e} />
                  </ListItem>
                );
              case FormElementKind.Date:
                return (
                  <ListItem key={index} className={classes.listItem}>
                    <Date componentType={e} />
                  </ListItem>
                );
              case FormElementKind.Media:
                return (
                  <ListItem key={index} className={classes.listItem}>
                    <ImageAndVideos componentType={e} />
                  </ListItem>
                );
              case FormElementKind.MediaUrl:
                return (
                  <ListItem key={index} className={classes.listItem}>
                    <MediaUrl componentType={e} />
                  </ListItem>
                );
              case FormElementKind.File:
                return (
                  <ListItem key={index} className={classes.listItem}>
                    <Files componentType={e} />
                  </ListItem>
                );
              case FormElementKind.Checkbox:
                return (
                  <ListItem key={index} className={classes.listItem}>
                    <Affirmation componentType={e} />
                  </ListItem>
                );
              case FormElementKind.Signature:
                return (
                  <ListItem key={index} className={classes.listItem}>
                    <Signature componentType={e} />
                  </ListItem>
                );
            }
          })}
      </List>
    </Box>
  );
};

export default ComponentsCard;
